import { useSize } from 'ahooks';
import type { FC } from 'react';
import { useMemo, useRef } from 'react';
import { cn } from '@/app/utils';

export interface CropPercent {
  x: number; // 0~1
  y: number; // 0~1
  width: number; // 0~1
  height: number; // 0~1
}

type PreviewCropProps = {
  imageUrl: string;
  cropPercent: CropPercent;
  naturalSize: { width: number; height: number };
  className?: string;
};

const EPS = 1e-6;
const safe = (v: number) => (Number.isFinite(v) ? v : 0);

/**
 * 以 CSS 背景定位和缩放的方式进行裁剪区域的实时预览。
 */
export const PreviewCrop: FC<PreviewCropProps> = ({ imageUrl, cropPercent, naturalSize, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const size = useSize(containerRef);

  const cw = size?.width ?? 0;
  const ch = size?.height ?? 0;

  // 使用 useMemo 优化样式计算
  const styles = useMemo(() => {
    // 将裁剪参数钳制到 [0,1]，防止越界
    const w = Math.min(1, Math.max(EPS, safe(cropPercent.width)));
    const h = Math.min(1, Math.max(EPS, safe(cropPercent.height)));
    const x = Math.min(1 - w, Math.max(0, safe(cropPercent.x)));
    const y = Math.min(1 - h, Math.max(0, safe(cropPercent.y)));

    const imgW = Math.max(1, naturalSize.width);
    const imgH = Math.max(1, naturalSize.height);

    if (cw <= 0 || ch <= 0) {
      return null;
    }

    // 基于裁剪区域与容器宽高比，采用 contain 逻辑确保裁剪区域完整可见并居中
    const cropAspect = (w * imgW) / (h * imgH);
    const containerAspect = cw / ch;

    let cropDisplayWidth: number;
    let cropDisplayHeight: number;

    if (cropAspect >= containerAspect) {
      // 裁剪区域更"宽"，以容器宽度为基准
      cropDisplayWidth = cw;
      cropDisplayHeight = cw / cropAspect;
    } else {
      // 裁剪区域更"高"，以容器高度为基准
      cropDisplayHeight = ch;
      cropDisplayWidth = ch * cropAspect;
    }

    // 推导背景图在容器中的实际尺寸（与裁剪百分比一致缩放）
    // 使用整数像素减少亚像素模糊
    const bgWidthPx = Math.round(cropDisplayWidth / w);
    const bgHeightPx = Math.round(cropDisplayHeight / h);
    const posX = Math.round(-x * bgWidthPx);
    const posY = Math.round(-y * bgHeightPx);

    return {
      cropDisplay: { width: cropDisplayWidth, height: cropDisplayHeight },
      backgroundSize: `${bgWidthPx}px ${bgHeightPx}px`,
      backgroundPosition: `${posX}px ${posY}px`,
    };
  }, [cw, ch, cropPercent, naturalSize]);

  return (
    <div ref={containerRef} className={cn('flex size-full items-center justify-center bg-transparent', className)}>
      {styles && (
        <div
          style={{
            width: `${styles.cropDisplay.width}px`,
            height: `${styles.cropDisplay.height}px`,
            backgroundImage: `url(${imageUrl})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: styles.backgroundSize,
            backgroundPosition: styles.backgroundPosition,
          }}
        />
      )}
    </div>
  );
};
